import React, { useEffect, useState } from 'react';
import { UsersIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { usersAPI, invitationsAPI } from '../services/api.ts';

export const Users: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'user' | 'viewer'>('user');
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const res = await usersAPI.getAll();
      setUsers(res.data.users);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviting(true);
    setError(null);
    setSuccess(null);
    try {
      await invitationsAPI.create({ email: inviteEmail, name: inviteName, role: inviteRole });
      setSuccess('Invitation sent!');
      setInviteEmail('');
      setInviteName('');
      setInviteRole('user');
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to send invite');
    } finally {
      setInviting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center">
        <UsersIcon className="h-8 w-8 text-primary-600 mr-3" />
        <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="card lg:col-span-2">
          <div className="card-body">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Team Members</h2>
            </div>
            {loading ? (
              <div className="py-12 text-center text-gray-500">Loading users...</div>
            ) : (
              <div className="divide-y">
                {users.map(u => (
                  <div key={u.id} className="py-3 flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900">{u.name}</div>
                      <div className="text-gray-600 text-sm">{u.email}</div>
                    </div>
                    <span className="badge badge-info">{u.role}</span>
                  </div>
                ))}
                {users.length === 0 && <div className="py-8 text-center text-gray-500">No users yet.</div>}
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2"><EnvelopeIcon className="h-5 w-5"/> Invite a user</h2>
            {error && <div className="mb-3 text-sm text-red-600">{error}</div>}
            {success && <div className="mb-3 text-sm text-green-600">{success}</div>}
            <form onSubmit={handleInvite} className="space-y-3">
              <div>
                <label className="form-label">Email</label>
                <input type="email" className="form-input" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} required />
              </div>
              <div>
                <label className="form-label">Name (optional)</label>
                <input type="text" className="form-input" value={inviteName} onChange={e => setInviteName(e.target.value)} />
              </div>
              <div>
                <label className="form-label">Role</label>
                <select className="form-input" value={inviteRole} onChange={e => setInviteRole(e.target.value as any)}>
                  <option value="user">User</option>
                  <option value="viewer">Viewer</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <button type="submit" className="btn btn-primary w-full" disabled={inviting}>
                {inviting ? 'Sending...' : 'Send Invite'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
